import { create } from 'zustand';
import { fetchStrapiCollection } from '@/lib/strapi';
import { normalizeInsuranceName, getInsuranceKey, dedupeByInsuranceName } from '@/lib/insuranceName';

const baseUrl = '/api/strapi';

export interface Insurance {
  id: number;
  documentId: string;
  name: string;
}

interface InsuranceState {
  insurances: Insurance[];
  loading: boolean;
  fetchInsurances: () => Promise<void>;
  createInsurance: (name: string) => Promise<Insurance | null>;
  createInsurancesFromList: (names: string[]) => Promise<void>;
  deleteInsurance: (documentId: string) => Promise<boolean>;
}

export const useInsuranceStore = create<InsuranceState>((set, get) => ({
  insurances: [],
  loading: false,

  fetchInsurances: async () => {
    try {
      set({ loading: true });
      const rawInsurances = await fetchStrapiCollection<{ id: number; documentId: string; name: string }>(
        'insurances',
        { filters: { deletedAt: { $null: true } } },
      );
      const insurances: Insurance[] = dedupeByInsuranceName(
        rawInsurances
          .map((item) => ({
            id: item.id,
            documentId: item.documentId,
            name: normalizeInsuranceName(item.name),
          }))
          .filter((item) => item.name.length > 0)
      );
      set({ insurances, loading: false });
    } catch (error) {
      console.error('src/store/insuranceStore.ts', error);
      set({ loading: false });
    }
  },

  createInsurance: async (name: string) => {
    try {
      const normalizedName = normalizeInsuranceName(name);
      if (!normalizedName) return null;

      const existing = get().insurances.find(
        (ins) => getInsuranceKey(ins.name) === getInsuranceKey(normalizedName)
      );
      if (existing) return existing;

      const res = await fetch(`${baseUrl}/insurances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { name: normalizedName } }),
      });

      if (!res.ok) {
        // If create fails (e.g., unique conflict), re-fetch to ensure local options stay in sync.
        await get().fetchInsurances();
        const synced = get().insurances.find(
          (ins) => getInsuranceKey(ins.name) === getInsuranceKey(normalizedName)
        );
        return synced || null;
      }

      const json = await res.json();
      const newInsurance: Insurance = {
        id: json.data.id,
        documentId: json.data.documentId,
        name: normalizeInsuranceName(json.data.name),
      };

      set((state) => ({ insurances: dedupeByInsuranceName([...state.insurances, newInsurance]) }));
      return newInsurance;
    } catch (error) {
      console.error('src/store/insuranceStore.ts', error);
      return null;
    }
  },

  createInsurancesFromList: async (names: string[]) => {
    const { createInsurance } = get();
    const uniqueNames: string[] = [];
    const seen = new Set<string>();

    for (const name of names) {
      const normalizedName = normalizeInsuranceName(name);
      const key = getInsuranceKey(normalizedName);

      if (!key || seen.has(key)) {
        continue;
      }

      seen.add(key);
      uniqueNames.push(normalizedName);
    }

    for (const name of uniqueNames) {
      await createInsurance(name);
    }

    await get().fetchInsurances();
  },

  deleteInsurance: async (documentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/insurances/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            deletedAt: new Date().toISOString(),
          },
        }),
      });

      if (!res.ok) return false;

      set((state) => ({
        insurances: state.insurances.filter((ins) => ins.documentId !== documentId),
      }));
      return true;
    } catch (error) {
      console.error('src/store/insuranceStore.ts', error);
      return false;
    }
  },
}));

